/**
 * LumenSales - Agents Page
 * InfinitAgents integration - List agents, configure autopilot
 *
 * Redesigned:
 * - Simple toggle for auto-respond to new contacts
 * - Dashboard-style view of attended contacts
 * - Auto-save on toggle changes
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Loader2, AlertCircle, CheckCircle2, X, Zap, Info, GraduationCap, Clock, Star } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import authService from '../../services/global/authService';
import api from '../../services/global/api';
import { markAgentTrained } from '../../hooks/useAgentTrainingNotification';
import { templateRequiresParams } from '../../utils/templateVariables';
import AgentGreetingModal from '../../components/agents/AgentGreetingModal';
import NewAgentModeModal from '../../components/agents/NewAgentModeModal';
import templateService from '../../services/global/templateService';
import useFeatureFlags from '../../hooks/useFeatureFlags';
import TemplatePicker from '../../components/messages/TemplatePicker';
import AgentScheduleEditor, { withWeekly } from '../../components/agents/AgentScheduleEditor';
import { templateCapabilities } from '../../utils/templateParams';

const AgentsPage = ({ isNavOpen, onToggleNav, autoOpenTrainingFor, onAutoOpenConsumed }) => {
  const { colors } = useTheme();
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();

  // State
  const [agents, setAgents] = useState([]);
  const [activeAgentId, setActiveAgentId] = useState(null);
  // Multi-agentes: o PRINCIPAL atende contatos novos/sem responsável; os
  // demais atendem os contatos atribuídos a eles.
  const [primaryAgentId, setPrimaryAgentId] = useState(null);
  const [settingPrimary, setSettingPrimary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/agents/primary')
      .then((r) => setPrimaryAgentId(r.data?.data?.primary_agent_id || null))
      .catch(() => {});
  }, []);

  const handleSetPrimary = async (agentId) => {
    setSettingPrimary(agentId);
    try {
      await api.put('/agents/primary', { agent_id: agentId });
      setPrimaryAgentId(agentId);
      setActiveAgentId(agentId);
    } catch (err) {
      console.error('[AgentsPage] set primary:', err);
    } finally {
      setSettingPrimary(null);
    }
  };

  // Mensagem de chegada (transferência): modal compartilhado AgentGreetingModal.
  const [greetingFor, setGreetingFor] = useState(null); // agente em edição
  const openGreeting = (agent) => setGreetingFor(agent);

  // Modal de criação de agente adicional: guiado (onboarding) ou do zero.
  const [showNewAgentModal, setShowNewAgentModal] = useState(false);

  // Popup state
  const [showPopup, setShowPopup] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [pulseAgentId, setPulseAgentId] = useState(null);
  const [autopilotMode, setAutopilotMode] = useState('off');
  const [autopilotContacts, setAutopilotContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  // Buffer/debounce: janela (segundos) antes da resposta do agente.
  const [agentBufferSeconds, setAgentBufferSeconds] = useState(0);
  const [savingBuffer, setSavingBuffer] = useState(false);
  const [bufferSaved, setBufferSaved] = useState(false);

  // Schedule state
  const [autopilotSchedule, setAutopilotSchedule] = useState({ enabled: false, slots: [{ days: [], start_time: '18:00', end_time: '07:00' }] });
  const [scheduleDebounceTimer, setScheduleDebounceTimer] = useState(null);

  // Mensagem de abertura (template Business) do PRÓPRIO número: usado quando
  // ESTE número é o número-casa de um agente e recebe um contato transferido de
  // outro número. WhatsApp Business não inicia a frio → abre via template
  // aprovado. (No QR/Evolution não é usado — manda texto livre direto.)
  const [handoffTemplate, setHandoffTemplate] = useState(null); // {name, language} | null
  const [businessTemplates, setBusinessTemplates] = useState([]);
  const [savingHandoff, setSavingHandoff] = useState(false);
  // Seletor único (2026-09-14): lista todos os aprovados e desabilita com o
  // motivo o que a abertura ainda não envia. Sem a flag, como antes.
  const { flags: featureFlags } = useFeatureFlags();
  const templateUi = Boolean(featureFlags.template_params_ui);
  const [allBusinessTemplates, setAllBusinessTemplates] = useState([]);
  const [handoffError, setHandoffError] = useState('');

  /**
   * Fetch agents from InfinitAgents API
   */
  const fetchAgents = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.getAgents();
      setAgents(response.data || []);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Erro ao carregar agentes';
      setError(errorMessage);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load autopilot config
   */
  const loadAutopilotConfig = useCallback(async () => {
    try {
      const response = await authService.getAutopilotConfig();
      if (response?.data) {
        setAutopilotMode(response.data.autopilot_mode || 'off');
        setAutopilotContacts(response.data.autopilot_contacts || []);
        setActiveAgentId(response.data.active_agent_id || null);
        setAgentBufferSeconds(response.data.agent_buffer_seconds || 0);
        if (response.data.autopilot_schedule) {
          // Horário por dia: `weekly` é a fonte da tela; doc antigo (só
          // `slots`) é convertido na hora e salvo no formato novo ao editar.
          setAutopilotSchedule(withWeekly(response.data.autopilot_schedule));
        }
      }
    } catch (err) {
      console.error('Error loading autopilot config:', err);
    }
  }, []);

  // Salva a janela de buffer (segundos). Persistido por workspace.
  const handleSaveBuffer = useCallback(async (seconds) => {
    const val = Math.max(0, Math.min(120, Number(seconds) || 0));
    setAgentBufferSeconds(val);
    setSavingBuffer(true);
    setBufferSaved(false);
    try {
      const resp = await authService.updateAgentBuffer(val);
      // Confirma com o valor que o backend persistiu (evita falso "salvo").
      const saved = resp?.data?.agent_buffer_seconds;
      if (saved !== undefined && saved !== null) setAgentBufferSeconds(saved);
      setBufferSaved(true);
      setTimeout(() => setBufferSaved(false), 2500);
    } catch (err) {
      console.error('Error saving agent buffer:', err);
    } finally {
      setSavingBuffer(false);
    }
  }, []);

  useEffect(() => {
    const hasApiKey = !!user?.infinitagents_api_key;
    if (hasApiKey) {
      fetchAgents();
      loadAutopilotConfig();
    } else {
      setLoading(false);
    }
  }, [user?.infinitagents_api_key, fetchAgents, loadAutopilotConfig]);

  // Carrega o template de abertura do próprio número + a lista de templates
  // APROVADOS (só faz sentido em número WhatsApp Business; no QR/Evolution a
  // lista vem vazia e a seção mostra o aviso).
  useEffect(() => {
    if (!user?.infinitagents_api_key) return;
    api.get('/agents/handoff-template')
      .then((r) => setHandoffTemplate(r.data?.data || null))
      .catch(() => {});
    templateService.list()
      .then((res) => {
        // Template de handoff sai sem components — só sem variável funciona.
        const list = (res?.templates || []).filter(
          (t) => (t.status || '').toUpperCase() === 'APPROVED' && !templateRequiresParams(t)
        );
        setBusinessTemplates(list);
        setAllBusinessTemplates(res?.templates || []);
      })
      .catch(() => setBusinessTemplates([]));
  }, [user?.infinitagents_api_key]);

  const handleSaveHandoffTemplate = async (name) => {
    setSavingHandoff(true);
    setHandoffError('');
    try {
      const tmpl = (templateUi ? allBusinessTemplates : businessTemplates).find((t) => t.name === name);
      const payload = name
        ? { name, language: tmpl?.language || 'pt_BR' }
        : { name: '' };
      const r = await api.put('/agents/handoff-template', payload);
      setHandoffTemplate(r.data?.data || null);
    } catch (err) {
      console.error('[AgentsPage] save handoff template:', err);
      if (templateUi) {
        const detail = err?.response?.data?.detail;
        setHandoffError(typeof detail === 'string' ? detail : 'Não foi possível salvar a mensagem de abertura.');
      }
    } finally {
      setSavingHandoff(false);
    }
  };

  // Auto-pulse "Treinar Agente" button on the agent card (triggered from training notification)
  useEffect(() => {
    if (!autoOpenTrainingFor) return;
    if (!agents || agents.length === 0) return;
    const target = agents.find((a) => a.id === activeAgentId) || agents[0];
    if (!target) return;
    setPulseAgentId(target.id);
    onAutoOpenConsumed?.();
  }, [autoOpenTrainingFor, agents, activeAgentId, onAutoOpenConsumed]);

  /**
   * Handle agent card click → open popup
   */
  const handleAgentClick = (agent) => {
    setSelectedAgent(agent);
    setShowPopup(true);
  };

  /**
   * Handle "Configurar agente" button → redirect to onboarding
   */
  const handleSetupAgent = () => {
    const cache = localStorage.getItem('lumensales-onboarding-cache');
    if (cache) {
      try {
        const parsed = JSON.parse(cache);
        const savedAt = new Date(parsed.savedAt);
        const daysSince = (Date.now() - savedAt.getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince > 7) {
          localStorage.removeItem('lumensales-onboarding-cache');
        }
      } catch {
        localStorage.removeItem('lumensales-onboarding-cache');
      }
    }
    navigate('/agent-onboarding');
  };

  /**
   * Toggle auto-respond to all new contacts (auto-save)
   */
  const handleToggleAutoRespond = async () => {
    // Apenas off/all selecionáveis na UI. O modo "specific" continua existindo
    // no backend (entrada-automática + transferência via toggle_autopilot_contact),
    // mas não é mais ligado/desligado por aqui.
    const newMode = autopilotMode === 'all' ? 'off' : 'all';

    setSaving(true);
    try {
      const response = await authService.updateAutopilotConfig(newMode, autopilotContacts);
      if (response?.data) {
        setAutopilotMode(response.data.autopilot_mode || 'off');
        setAutopilotContacts(response.data.autopilot_contacts || []);
      }
    } catch (err) {
      console.error('Error toggling auto-respond:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Toggle scheduled mode (auto-save)
   */
  const handleToggleScheduled = async () => {
    const newMode = autopilotMode === 'scheduled' ? 'off' : 'scheduled';
    const scheduleToSend = newMode === 'scheduled'
      ? { ...autopilotSchedule, enabled: true }
      : { ...autopilotSchedule, enabled: false };

    setSaving(true);
    try {
      const response = await authService.updateAutopilotConfig(newMode, autopilotContacts, scheduleToSend);
      if (response?.data) {
        setAutopilotMode(response.data.autopilot_mode || 'off');
        if (response.data.autopilot_schedule) {
          setAutopilotSchedule(response.data.autopilot_schedule);
        }
      }
    } catch (err) {
      console.error('Error toggling scheduled mode:', err);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Horário por dia e regra de fora do horário (AgentScheduleEditor): o
   * componente devolve o schedule inteiro já com `slots` derivado de `weekly`.
   */
  const handleScheduleChange = (newSchedule) => {
    setAutopilotSchedule(newSchedule);
    debounceSaveSchedule(newSchedule);
  };

  /**
   * Debounced save for schedule changes
   */
  const debounceSaveSchedule = (schedule) => {
    if (scheduleDebounceTimer) clearTimeout(scheduleDebounceTimer);
    const timer = setTimeout(async () => {
      try {
        await authService.updateAutopilotConfig('scheduled', autopilotContacts, { ...schedule, enabled: true });
      } catch (err) {
        console.error('Error saving schedule:', err);
      }
    }, 800);
    setScheduleDebounceTimer(timer);
  };

  /**
   * Check if agent is currently active based on schedule (client-side check)
   */
  const isScheduleActiveNow = () => {
    if (autopilotMode !== 'scheduled' || !autopilotSchedule.enabled) return false;
    const slots = autopilotSchedule.slots || [];
    if (slots.length === 0) return false;

    const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
    const currentDay = now.getDay(); // 0=Sun
    const currentTime = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
    const yesterday = (currentDay + 6) % 7;

    // Mesma regra do backend (is_within_schedule): qualquer slot vale.
    return slots.some(({ start_time, end_time, days = [] }) => {
      if (!start_time || !end_time) return false;
      if (start_time <= end_time) {
        return days.includes(currentDay) && currentTime >= start_time && currentTime <= end_time;
      }
      if (days.includes(currentDay) && currentTime >= start_time) return true;
      return days.includes(yesterday) && currentTime < end_time;
    });
  };

  /**
   * Get avatar initials from agent name
   */
  const getInitials = (name) => {
    if (!name) return 'AG';
    const words = name.trim().split(' ');
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  /**
   * Get avatar color based on agent name
   */
  const getAvatarColor = (name) => {
    const avatarColors = [
      '#8B5CF6', '#EC4899', '#3B82F6', '#10B981',
      '#F59E0B', '#EF4444', '#06B6D4', '#6366F1',
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return avatarColors[Math.abs(hash) % avatarColors.length];
  };

  /**
   * Truncate text with ellipsis
   */
  const truncate = (text, maxLength = 80) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const hasApiKey = !!user?.infinitagents_api_key;
  const isAutoRespondOn = autopilotMode === 'all';
  const isScheduledOn = autopilotMode === 'scheduled';

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.bgSecondary,
      height: '100vh',
      overflow: 'hidden',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '10px 24px',
      backgroundColor: colors.bgPrimary,
      boxShadow: colors.shadowSm,
      minHeight: '60px',
      zIndex: 1,
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    headerIcon: {
      width: '40px',
      height: '40px',
      backgroundColor: colors.accentPrimary,
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
    },
    headerTitle: {
      display: 'flex',
      flexDirection: 'column',
    },
    title: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
      margin: 0,
    },
    subtitle: {
      fontSize: '13px',
      color: colors.textSecondary,
      margin: 0,
    },
    content: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto',
    },
    infoBox: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '12px',
      padding: '16px 20px',
      backgroundColor: `${colors.accentPrimary}10`,
      borderRadius: '12px',
      marginBottom: '24px',
      border: `1px solid ${colors.accentPrimary}30`,
    },
    infoIcon: {
      color: colors.accentPrimary,
      flexShrink: 0,
      marginTop: '2px',
    },
    infoText: {
      fontSize: '14px',
      color: colors.textPrimary,
      lineHeight: 1.5,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
      gap: '16px',
    },
    card: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '12px',
      padding: '20px',
      border: `2px solid ${colors.borderColor}`,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      position: 'relative',
    },
    cardSelected: {
      borderColor: colors.accentPrimary,
    },
    cardSelectedAll: {
      borderColor: '#10B981',
    },
    cardContent: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
    },
    avatar: {
      width: '52px',
      height: '52px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontSize: '18px',
      fontWeight: 600,
      flexShrink: 0,
      overflow: 'hidden',
    },
    avatarImg: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
    },
    agentInfo: {
      flex: 1,
      minWidth: 0,
    },
    agentName: {
      fontSize: '16px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '6px',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    selectedBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '11px',
      fontWeight: 500,
      color: colors.accentPrimary,
      backgroundColor: `${colors.accentPrimary}15`,
      padding: '2px 8px',
      borderRadius: '10px',
    },
    agentDesc: {
      fontSize: '13px',
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    // Center states
    centerState: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '48px',
      textAlign: 'center',
    },
    stateIcon: {
      width: '64px',
      height: '64px',
      borderRadius: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: '20px',
    },
    stateTitle: {
      fontSize: '18px',
      fontWeight: 600,
      color: colors.textPrimary,
      marginBottom: '8px',
    },
    stateText: {
      fontSize: '14px',
      color: colors.textSecondary,
      maxWidth: '400px',
      lineHeight: 1.5,
    },
    setupBtn: {
      marginTop: '20px',
      padding: '12px 28px',
      backgroundColor: colors.accentPrimary,
      color: 'white',
      border: 'none',
      borderRadius: '10px',
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    loader: {
      animation: 'spin 1s linear infinite',
    },
    // Popup / Modal
    overlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    popup: {
      backgroundColor: colors.bgPrimary,
      borderRadius: '16px',
      width: '100%',
      maxWidth: '520px',
      maxHeight: '85vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      border: `1px solid ${colors.borderColor}`,
      boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    },
    popupHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '20px 24px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    popupHeaderInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
    },
    popupBody: {
      flex: 1,
      padding: '24px',
      overflowY: 'auto',
    },
    closeBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '6px',
      borderRadius: '8px',
      color: colors.textSecondary,
      display: 'flex',
      alignItems: 'center',
    },
    autopilotBadge: {
      fontSize: '11px',
      fontWeight: 500,
      padding: '3px 10px',
      borderRadius: '10px',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
    },
    // Toggle switch
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 20px',
      backgroundColor: colors.bgSecondary,
      borderRadius: '12px',
      marginBottom: '8px',
    },
    toggleLabel: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      flex: 1,
      marginRight: '16px',
    },
    toggleTitle: {
      fontSize: '15px',
      fontWeight: 600,
      color: colors.textPrimary,
    },
    toggleDesc: {
      fontSize: '12px',
      color: colors.textSecondary,
      lineHeight: 1.4,
    },
    toggleSwitch: {
      width: '50px',
      height: '28px',
      borderRadius: '14px',
      padding: '2px',
      cursor: 'pointer',
      transition: 'background-color 0.2s',
      border: 'none',
      flexShrink: 0,
      display: 'flex',
      alignItems: 'center',
    },
    toggleKnob: {
      width: '24px',
      height: '24px',
      borderRadius: '50%',
      backgroundColor: 'white',
      transition: 'transform 0.2s',
      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    },
  };

  // ============================================
  // RENDER: Header
  // ============================================

  const renderHeader = () => (
    <div style={styles.header}>
      <div style={styles.headerLeft}>
        <div style={styles.headerIcon}>
          <Bot size={20} />
        </div>
        <div style={styles.headerTitle}>
          <h1 style={styles.title}>Meus Agentes</h1>
          <p style={styles.subtitle}>Configure o atendimento automático</p>
        </div>
      </div>
      {/* Multi-agentes: cria um agente ADICIONAL sem mexer no principal.
          O modal deixa escolher: guiado (onboarding ?additional=1) ou do
          zero (cria vazio e abre a tela de treino direto). */}
      <button
        onClick={() => setShowNewAgentModal(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 10, border: 'none',
          backgroundColor: colors.accentPrimary, color: '#fff',
          fontSize: 13, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
        }}
      >
        <Bot size={15} />
        Adicionar agente
      </button>
    </div>
  );

  // ============================================
  // RENDER: Popup with toggle + dashboard
  // ============================================

  const renderPopup = () => {
    if (!showPopup || !selectedAgent) return null;

    const logoUrl = selectedAgent.widget_settings?.logo_url;

    return (
      <div style={styles.overlay} onClick={() => setShowPopup(false)}>
        <div style={styles.popup} onClick={e => e.stopPropagation()}>
          {/* Header */}
          <div style={styles.popupHeader}>
            <div style={styles.popupHeaderInfo}>
              <div
                style={{
                  ...styles.avatar,
                  width: '44px',
                  height: '44px',
                  fontSize: '16px',
                  backgroundColor: logoUrl ? 'transparent' : getAvatarColor(selectedAgent.name),
                }}
              >
                {logoUrl ? (
                  <img src={logoUrl} alt={selectedAgent.name} style={styles.avatarImg} />
                ) : (
                  getInitials(selectedAgent.name)
                )}
              </div>
              <div>
                <div style={{ fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
                  {selectedAgent.name}
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '2px' }}>
                  {truncate(selectedAgent.description, 60) || 'Sem descrição'}
                </div>
              </div>
            </div>
            <button style={styles.closeBtn} onClick={() => setShowPopup(false)}>
              <X size={20} />
            </button>
          </div>

          {/* Body */}
          <div style={styles.popupBody}>
            {/* Auto-respond toggle */}
            <div style={styles.toggleRow}>
              <div style={styles.toggleLabel}>
                <span style={styles.toggleTitle}>Responder novos contatos</span>
                <span style={styles.toggleDesc}>
                  O agente responde quem entrar em contato a partir de agora. Quem já está no inbox continua com a equipe, a menos que seja transferido para o agente
                </span>
              </div>
              <button
                style={{
                  ...styles.toggleSwitch,
                  backgroundColor: isAutoRespondOn ? colors.accentPrimary : colors.borderColor,
                  opacity: saving ? 0.6 : 1,
                  pointerEvents: saving ? 'none' : 'auto',
                }}
                onClick={handleToggleAutoRespond}
              >
                <div style={{
                  ...styles.toggleKnob,
                  transform: isAutoRespondOn ? 'translateX(22px)' : 'translateX(0px)',
                }} />
              </button>
            </div>

            {/* Info banner when auto-respond is ON */}
            {isAutoRespondOn && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 14px',
                backgroundColor: '#10B98110',
                borderRadius: '8px',
                marginBottom: '4px',
                border: '1px solid #10B98125',
              }}>
                <Info size={14} style={{ color: '#10B981', flexShrink: 0 }} />
                <span style={{ fontSize: '12px', color: '#10B981', lineHeight: 1.4 }}>
                  Todos os contatos que enviarem mensagem serão atendidos automaticamente pelo agente.
                </span>
              </div>
            )}

            {/* Scheduled mode toggle */}
            <div style={{ ...styles.toggleRow, marginTop: '4px' }}>
              <div style={styles.toggleLabel}>
                <span style={styles.toggleTitle}>
                  <Clock size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Horários programados
                </span>
                <span style={styles.toggleDesc}>
                  O agente responde apenas nos dias e horários definidos
                </span>
              </div>
              <button
                style={{
                  ...styles.toggleSwitch,
                  backgroundColor: isScheduledOn ? colors.accentPrimary : colors.borderColor,
                  opacity: saving ? 0.6 : 1,
                  pointerEvents: saving ? 'none' : 'auto',
                }}
                onClick={handleToggleScheduled}
              >
                <div style={{
                  ...styles.toggleKnob,
                  transform: isScheduledOn ? 'translateX(22px)' : 'translateX(0px)',
                }} />
              </button>
            </div>

            {/* Buffer / tempo de resposta do agente */}
            <div style={{ ...styles.toggleRow, marginTop: '4px', alignItems: 'flex-start' }}>
              <div style={styles.toggleLabel}>
                <span style={styles.toggleTitle}>
                  <Clock size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Tempo de resposta (buffer)
                  {savingBuffer && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: colors.textSecondary, fontWeight: 400 }}>
                      salvando…
                    </span>
                  )}
                  {bufferSaved && !savingBuffer && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: '#10B981', fontWeight: 600 }}>
                      ✓ Salvo
                    </span>
                  )}
                </span>
                <span style={styles.toggleDesc}>
                  Aguarda alguns segundos juntando mensagens picadas do mesmo
                  contato antes de o agente responder. 0 = responde na hora.
                </span>
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
                  {[0, 5, 7, 10, 15].map((s) => (
                    <button
                      key={s}
                      onClick={() => handleSaveBuffer(s)}
                      disabled={savingBuffer}
                      style={{
                        padding: '5px 12px',
                        borderRadius: '8px',
                        border: `1px solid ${agentBufferSeconds === s ? colors.accentPrimary : colors.borderColor}`,
                        backgroundColor: agentBufferSeconds === s ? `${colors.accentPrimary}18` : 'transparent',
                        color: agentBufferSeconds === s ? colors.accentPrimary : colors.textSecondary,
                        fontSize: '12px',
                        fontWeight: 600,
                        cursor: savingBuffer ? 'progress' : 'pointer',
                      }}
                    >
                      {s === 0 ? 'Desligado' : `${s}s`}
                    </button>
                  ))}
                  <input
                    type="number"
                    min={0}
                    max={120}
                    value={agentBufferSeconds}
                    onChange={(e) => setAgentBufferSeconds(Number(e.target.value))}
                    onBlur={(e) => handleSaveBuffer(e.target.value)}
                    style={{
                      width: '64px',
                      padding: '5px 8px',
                      borderRadius: '8px',
                      border: `1px solid ${colors.borderColor}`,
                      backgroundColor: colors.bgSecondary,
                      color: colors.textPrimary,
                      fontSize: '12px',
                    }}
                    title="Segundos (0 a 120)"
                  />
                </div>
              </div>
            </div>

            {/* Horário por dia + fora do horário (visível com Horários programados ligado) */}
            {isScheduledOn && (
              <AgentScheduleEditor
                schedule={autopilotSchedule}
                onChange={handleScheduleChange}
                activeNow={isScheduleActiveNow()}
                colors={colors}
                offHoursEnabled={Boolean(featureFlags.autopilot_off_hours)}
                businessTemplates={businessTemplates}
                hasBusiness={allBusinessTemplates.length > 0}
              />
            )}

            {/* Mensagem de abertura (transferência cross-número via WhatsApp Business) */}
            <div style={{ ...styles.toggleRow, marginTop: '4px', flexDirection: 'column', alignItems: 'stretch', gap: '8px' }}>
              <div style={styles.toggleLabel}>
                <span style={styles.toggleTitle}>
                  <Zap size={15} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                  Mensagem de abertura (WhatsApp Business)
                  {savingHandoff && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: colors.textSecondary, fontWeight: 400 }}>
                      salvando…
                    </span>
                  )}
                </span>
                <span style={styles.toggleDesc}>
                  Template aprovado enviado por ESTE número quando ele recebe um
                  contato transferido de outro número. Necessário no WhatsApp
                  Business — o número não pode iniciar a conversa a frio.
                </span>
              </div>
              {templateUi ? (
                <>
                  <TemplatePicker
                    templates={allBusinessTemplates}
                    value={handoffTemplate?.name || ''}
                    language={handoffTemplate?.language || ''}
                    onChange={(tpl) => handleSaveHandoffTemplate(tpl?.name || '')}
                    capabilities={templateCapabilities('handoff', featureFlags)}
                    colors={colors}
                    disabled={savingHandoff}
                    noneLabel="Nenhuma (sem mensagem de abertura)"
                    emptyHint="Nenhum template aprovado neste número. Crie e aprove um template na área de WhatsApp Business para usar como abertura."
                  />
                  {handoffError && (
                    <span style={{ fontSize: 12, color: colors.danger || '#ef4444', lineHeight: 1.4 }}>
                      {handoffError}
                    </span>
                  )}
                </>
              ) : businessTemplates.length > 0 ? (
                <select
                  value={handoffTemplate?.name || ''}
                  onChange={(e) => handleSaveHandoffTemplate(e.target.value)}
                  disabled={savingHandoff}
                  style={{
                    width: '100%',
                    padding: '8px 10px',
                    backgroundColor: colors.bgPrimary,
                    color: colors.textPrimary,
                    border: `1px solid ${colors.borderColor}`,
                    borderRadius: '8px',
                    fontSize: '13px',
                    outline: 'none',
                    boxSizing: 'border-box',
                    cursor: savingHandoff ? 'progress' : 'pointer',
                  }}
                >
                  <option value="">Nenhuma (sem mensagem de abertura)</option>
                  {businessTemplates.map((t) => (
                    <option key={t.name} value={t.name}>{t.name}</option>
                  ))}
                </select>
              ) : (
                <span style={{ fontSize: 12, color: colors.textTertiary, lineHeight: 1.5 }}>
                  Nenhum template aprovado neste número. Crie e aprove um template
                  na área de WhatsApp Business para usar como abertura.
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================
  // RENDER: States
  // ============================================

  // Loading
  if (loading) {
    return (
      <div style={styles.container}>
        {renderHeader()}
        <div style={styles.centerState}>
          <Loader2 size={40} color={colors.accentPrimary} style={styles.loader} />
          <p style={{ ...styles.stateText, marginTop: '16px' }}>Carregando agentes...</p>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // No API Key or no agents
  if (!hasApiKey || agents.length === 0) {
    return (
      <div style={styles.container}>
        {renderHeader()}
        <div style={styles.centerState}>
          <div style={{ ...styles.stateIcon, backgroundColor: `${colors.accentPrimary}15` }}>
            <Bot size={32} color={colors.accentPrimary} />
          </div>
          <h2 style={styles.stateTitle}>
            {!hasApiKey ? 'Agente ainda não configurado' : 'Nenhum agente encontrado'}
          </h2>
          <p style={styles.stateText}>
            {!hasApiKey
              ? 'Parece que seu agente ainda não foi configurado. Clique abaixo para iniciar a configuração.'
              : 'Crie seu primeiro agente para começar a usar o atendimento automático.'
            }
          </p>
          <button style={styles.setupBtn} onClick={handleSetupAgent}>
            <Zap size={18} />
            Configurar agente
          </button>
        </div>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={styles.container}>
        {renderHeader()}
        <div style={styles.centerState}>
          <div style={{ ...styles.stateIcon, backgroundColor: 'rgba(239, 68, 68, 0.1)' }}>
            <AlertCircle size={32} color="#EF4444" />
          </div>
          <h2 style={styles.stateTitle}>Erro ao carregar agentes</h2>
          <p style={styles.stateText}>{error}</p>
          <button
            onClick={fetchAgents}
            style={{
              marginTop: '16px',
              padding: '10px 20px',
              backgroundColor: colors.accentPrimary,
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER: Agents grid
  // ============================================

  const getModeBadge = () => {
    if (autopilotMode === 'all') {
      return (
        <span style={{
          ...styles.autopilotBadge,
          color: '#10B981',
          backgroundColor: '#10B98115',
          fontWeight: 600,
          border: '1px solid #10B98130',
        }}>
          <Zap size={11} /> Respondendo todos
        </span>
      );
    }
    if (autopilotMode === 'specific') {
      return (
        <span style={{
          ...styles.autopilotBadge,
          color: colors.accentPrimary,
          backgroundColor: `${colors.accentPrimary}15`,
        }}>
          <Zap size={11} /> {autopilotContacts.length} contato{autopilotContacts.length !== 1 ? 's' : ''}
        </span>
      );
    }
    return (
      <span style={{
        ...styles.autopilotBadge,
        color: colors.textTertiary,
        backgroundColor: `${colors.textTertiary}15`,
      }}>
        Desligado
      </span>
    );
  };

  return (
    <div style={styles.container}>
      {renderHeader()}

      <div style={styles.content}>
        <div style={styles.infoBox}>
          <Bot size={20} style={styles.infoIcon} />
          <p style={styles.infoText}>
            <strong>Clique no agente</strong> para configurar o atendimento automático.
            Ative a resposta automática para novos contatos ou defina horários programados.
          </p>
        </div>

        <div style={styles.grid}>
          {agents.map((agent) => {
            const isSelected = activeAgentId === agent.id;
            const logoUrl = agent.widget_settings?.logo_url;

            return (
              <div
                key={agent.id}
                onClick={() => handleAgentClick(agent)}
                style={{
                  ...styles.card,
                  ...(isSelected && autopilotMode === 'all' ? styles.cardSelectedAll : isSelected ? styles.cardSelected : {}),
                }}
              >
                <div style={styles.cardContent}>
                  <div
                    style={{
                      ...styles.avatar,
                      backgroundColor: logoUrl ? 'transparent' : getAvatarColor(agent.name),
                    }}
                  >
                    {logoUrl ? (
                      <img
                        src={logoUrl}
                        alt={agent.name}
                        style={styles.avatarImg}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.parentElement.style.backgroundColor = getAvatarColor(agent.name);
                          e.target.parentElement.innerHTML = getInitials(agent.name);
                        }}
                      />
                    ) : (
                      getInitials(agent.name)
                    )}
                  </div>

                  <div style={styles.agentInfo}>
                    <div style={styles.agentName}>
                      {agent.name}
                      {(primaryAgentId ? primaryAgentId === agent.id : isSelected) && (
                        <span style={{
                          ...styles.selectedBadge,
                          color: '#d97706',
                          backgroundColor: 'rgba(245,158,11,0.14)',
                        }} title="Atende contatos novos e sem responsável">
                          <Star size={12} />
                          Principal
                        </span>
                      )}
                      {isSelected && (
                        <span style={{
                          ...styles.selectedBadge,
                          ...(autopilotMode === 'all' ? { color: '#10B981', backgroundColor: '#10B98115' } : {}),
                        }}>
                          <CheckCircle2 size={12} />
                          {autopilotMode === 'all' ? 'Ativo para todos' : 'Ativo'}
                        </span>
                      )}
                    </div>
                    <p style={styles.agentDesc}>
                      {truncate(agent.description) || 'Sem descrição'}
                    </p>
                    {isSelected && (
                      <div style={{ marginTop: '8px' }}>
                        {getModeBadge()}
                      </div>
                    )}
                  </div>
                </div>

                {/* Card actions: Treinar Agente + Configurações de atendimento */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '6px',
                    marginTop: '14px',
                  }}
                >
                  <button
                    className={pulseAgentId === agent.id ? 'pulse-train-btn' : undefined}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPulseAgentId(null);
                      markAgentTrained();
                      // Multi-agentes: treina ESTE agente (sections próprias).
                      navigate(`/agent-training?agent_id=${agent.id}`);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '10px 14px',
                      backgroundColor: `${colors.accentPrimary}10`,
                      color: colors.accentPrimary,
                      border: `1px solid ${colors.accentPrimary}30`,
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <GraduationCap size={16} />
                    Treinar Agente
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); openGreeting(agent); }}
                    title="Mensagem que este agente envia ao RECEBER um contato transferido"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      width: '100%',
                      padding: '10px 14px',
                      backgroundColor: 'transparent',
                      color: colors.textSecondary,
                      border: `1px solid ${colors.borderColor}`,
                      borderRadius: '10px',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <Zap size={14} />
                    Mensagem de chegada
                  </button>
                  {primaryAgentId && primaryAgentId !== agent.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSetPrimary(agent.id); }}
                      disabled={settingPrimary === agent.id}
                      title="O principal atende contatos novos e sem responsável"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        width: '100%',
                        padding: '10px 14px',
                        backgroundColor: 'transparent',
                        color: '#d97706',
                        border: '1px solid rgba(245,158,11,0.45)',
                        borderRadius: '10px',
                        fontSize: '13px',
                        fontWeight: 600,
                        cursor: settingPrimary === agent.id ? 'wait' : 'pointer',
                        transition: 'all 0.15s',
                      }}
                    >
                      {settingPrimary === agent.id
                        ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                        : <Star size={14} />}
                      Definir como principal
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAgentClick(agent);
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: colors.textSecondary,
                      fontSize: '12px',
                      fontWeight: 500,
                      cursor: 'pointer',
                      padding: '4px 8px',
                      textDecoration: 'underline',
                    }}
                  >
                    Configurações de atendimento
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {renderPopup()}

      {/* Novo agente: guiado (onboarding) ou do zero (tela de treino direto) */}
      <NewAgentModeModal
        isOpen={showNewAgentModal}
        onClose={() => setShowNewAgentModal(false)}
        onGuided={() => navigate('/agent-onboarding?additional=1')}
        onCreatedFromScratch={(agentId) => {
          setShowNewAgentModal(false);
          navigate(`/agent-training?agent_id=${agentId}`);
        }}
        colors={colors}
      />

      {/* Mensagem de chegada (transferência) — modal compartilhado */}
      {greetingFor && (
        <AgentGreetingModal
          agent={greetingFor}
          colors={colors}
          onClose={() => setGreetingFor(null)}
        />
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes pulseTrain {
          0%, 100% { box-shadow: 0 0 0 0 rgba(0, 168, 132, 0.28); }
          50%      { box-shadow: 0 0 0 6px rgba(0, 168, 132, 0); }
        }
        .pulse-train-btn {
          animation: pulseTrain 2.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default AgentsPage;
